'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import MobileStrategyPlayground from './MobileStrategyPlayground';

// --- CONFIGURATION ---
const MAX_CAPACITY = 100;

interface NodeConfig {
    id: string;
    label: string;
    icon: string;
    color: string; // Tailwind text color class
    gradient: string; // Tailwind gradient class
    shimmerColor: string; // Hex for path
    basePower: number;
    cost: number;
}

const NODES: NodeConfig[] = [
    { id: 'seo', label: 'Local SEO', icon: 'MapPinIcon', color: 'text-blue-600', gradient: 'from-blue-400 to-blue-600', shimmerColor: '#2563eb', basePower: 20, cost: 25 },
    { id: 'content', label: 'Content Strategy', icon: 'DocumentTextIcon', color: 'text-rose-500', gradient: 'from-rose-400 to-red-500', shimmerColor: '#f43f5e', basePower: 15, cost: 20 },
    { id: 'reputation', label: 'Reputation', icon: 'StarIcon', color: 'text-amber-500', gradient: 'from-amber-400 to-orange-500', shimmerColor: '#f59e0b', basePower: 30, cost: 35 },
    { id: 'social', label: 'Google Business Profile', icon: 'BuildingStorefrontIcon', color: 'text-blue-500', gradient: 'from-blue-400 to-cyan-500', shimmerColor: '#3b82f6', basePower: 15, cost: 20 },
    { id: 'analytics', label: 'Analytics', icon: 'PresentationChartLineIcon', color: 'text-indigo-600', gradient: 'from-indigo-400 to-violet-500', shimmerColor: '#6366f1', basePower: 25, cost: 30 },
];

function ShimmerPath({ path, color, progress }: { path: string, color: string, progress: any }) {
    // 20% shimmer length
    const shimmerLength = 20;
    const start = 100 + shimmerLength;
    const end = -shimmerLength;

    const strokeDashoffset = useTransform(progress, [0, 1], [start, end]);

    // Fade in/out at ends matches the Framer component feel
    const opacity = useTransform(progress, (val: number) => {
        if (val < 0.05) return val / 0.05;
        if (val > 0.95) return (1 - val) / 0.05;
        return 1;
    });

    return (
        <motion.path
            d={path}
            fill="none"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${shimmerLength} 100`}
            pathLength={100}
            style={{ strokeDashoffset, opacity }}
        />
    );
}

const StrategyPlayground = () => {
    // Default active nodes (simulating the "connected" state)
    const [activeNodes, setActiveNodes] = useState<string[]>(['seo', 'content', 'reputation', 'social', 'analytics']);

    // --- STATISTICS ---
    const stats = useMemo(() => {
        let currentCost = 0;
        let activePower = 0;
        activeNodes.forEach(id => {
            const node = NODES.find(n => n.id === id);
            if (node) {
                currentCost += node.cost;
                activePower += node.basePower;
            }
        });
        return {
            totalPower: Math.min(Math.round(activePower), 100)
        };
    }, [activeNodes]);

    // --- ANIMATION LOOP ---
    const progress = useMotionValue(0);
    useEffect(() => {
        // Continuous loop exactly like the logic in provided code
        const controls = animate(progress, 1, {
            duration: 2.5, // "3.5" speed setting equivalent
            ease: [0.95, 0.04, 0.44, 1], // "smooth" easing from code
            repeat: Infinity,
            repeatDelay: 0.8 // loopDelay
        });
        return controls.stop;
    }, [progress]);

    const toggleNode = (id: string) => {
        if (activeNodes.includes(id)) {
            setActiveNodes(prev => prev.filter(n => n !== id));
        } else {
            setActiveNodes(prev => [...prev, id]);
        }
    };

    // --- HORIZONTAL CURVE CALCULATIONS (Matching Framer logic) ---
    const width = 800;
    const height = 500;
    const iconPadding = 60;
    const startX = iconPadding + 40;
    const centerY = height / 2;
    const endX = width - iconPadding - 40;

    const spacing = 90; // Vertical spacing
    const pathCount = NODES.length;
    const maxSpread = (pathCount - 1) * spacing / 2;
    const curvature = 1.3;

    const generatePath = (index: number) => {
        // Logic from: AnimatedFlowPaths component
        const offset = index * spacing - maxSpread;

        const cp1X = startX + 120 * curvature;
        const cp1Y = centerY + offset * 0.2 * curvature; // Subtle control point curve
        const cp2X = endX - 150 * curvature;
        const cp2Y = centerY + offset * 0.8 * curvature; // Stronger arrival curve

        // M startX,centerY C cp1X,cp1Y cp2X,cp2Y endX,targetY
        return `M ${startX},${centerY} C ${cp1X},${cp1Y} ${cp2X},${cp2Y} ${endX},${centerY + offset}`;
    };

    return (
        <section className="bg-white relative overflow-hidden">
            {/* MOBILE VIEW */}
            <div className="block lg:hidden w-full">
                <MobileStrategyPlayground />
            </div>

            {/* DESKTOP VIEW */}
            <div className="hidden lg:flex py-24 min-h-[700px] flex-col items-center relative w-full">

                {/* Header */}
                <div className="text-center mb-10 relative z-10 w-full max-w-7xl px-6">
                    <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
                        Brand Visibility <span className="text-blue-600">Network</span>
                    </h2>
                    <p className="text-lg text-slate-500 max-w-2xl mx-auto">
                        Visualize how our integrated services drive exponential growth to your brand core.
                    </p>
                </div>

                {/* VISUALIZATION CONTAINER */}
                <div className="relative w-full max-w-5xl h-[500px] bg-slate-50/50 rounded-[40px] border border-slate-100 flex items-center justify-center">

                    {/* SVG Layer */}
                    <svg viewBox={`0 0 ${width} ${height}`} className="absolute inset-0 w-full h-full pointer-events-none z-0">
                        {NODES.map((node, i) => (
                            <React.Fragment key={`path-group-${i}`}>
                                {/* Base Path (Grey) */}
                                <path
                                    d={generatePath(i)}
                                    stroke="#e2e8f0"
                                    strokeWidth="3"
                                    fill="none"
                                    strokeLinecap="round"
                                />
                                {/* Shimmer Path (Colored) - only if active */}
                                {activeNodes.includes(node.id) && (
                                    <ShimmerPath
                                        path={generatePath(i)}
                                        color={node.shimmerColor}
                                        progress={progress}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                    </svg>

                    {/* --- DOM ELEMENTS --- */}

                    {/* 1. START NODE (Left) */}
                    <div
                        className="absolute z-20"
                        style={{ left: `${(startX / width) * 100}%`, top: '50%', transform: 'translate(-50%, -50%)' }}
                    >
                        <div className="w-24 h-24 rounded-2xl bg-white shadow-xl border-4 border-white flex flex-col items-center justify-center relative">
                            {/* Glow */}
                            <div className="absolute inset-0 bg-blue-500/10 blur-xl rounded-full" />

                            <div className="relative z-10 text-center">
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-violet-600 rounded-xl flex items-center justify-center mx-auto mb-1 text-white shadow-lg">
                                    <Icon name="SparklesIcon" size={24} />
                                </div>
                                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Brand Core</div>
                            </div>
                        </div>
                    </div>

                    {/* 2. END NODES (Right Column) */}
                    {NODES.map((node, i) => {
                        const offset = i * spacing - maxSpread;
                        const topPct = ((centerY + offset) / height) * 100;
                        const leftPct = (endX / width) * 100;
                        const isActive = activeNodes.includes(node.id);

                        return (
                            <div
                                key={node.id}
                                className="absolute z-20"
                                style={{
                                    left: `${leftPct}%`,
                                    top: `${topPct}%`,
                                    transform: 'translate(-50%, -50%)'
                                }}
                            >
                                <button
                                    onClick={() => toggleNode(node.id)}
                                    className={`group flex items-center gap-4 transition-all duration-300 ${isActive ? 'translate-x-0' : 'translate-x-2 opacity-70 grayscale hover:grayscale-0 hover:opacity-100'}`}
                                >
                                    {/* Icon Circle */}
                                    <div className={`w-14 h-14 rounded-full flex items-center justify-center bg-white shadow-lg border-2 transition-all duration-300 ${isActive ? 'border-white ring-4 ring-opacity-20' : 'border-slate-100'}`} style={{ boxShadow: isActive ? `0 4px 20px ${node.shimmerColor}30` : '' }}>
                                        <Icon name={node.icon as any} className={`transition-colors ${isActive ? node.color : 'text-slate-400'}`} size={24} />
                                    </div>

                                    {/* Label side */}
                                    <div className="text-left w-32 hidden md:block">
                                        <div className={`text-sm font-bold transition-colors ${isActive ? 'text-slate-900' : 'text-slate-500'}`}>
                                            {node.label}
                                        </div>
                                        <div className="text-[10px] text-slate-400">
                                            {isActive ? 'Active' : 'Connect'}
                                        </div>
                                    </div>
                                </button>
                            </div>
                        );
                    })}

                </div>
            </div>

        </section>
    );
};

export default StrategyPlayground;
